import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type {
  Equipment,
  MaintenanceSchedule,
  WorkOrder,
  Vendor,
  ApiResponse,
} from '@/types'

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const cmmsKeys = {
  equipment: {
    all: ['equipment'] as const,
    lists: () => [...cmmsKeys.equipment.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...cmmsKeys.equipment.lists(), filters] as const,
    details: () => [...cmmsKeys.equipment.all, 'detail'] as const,
    detail: (id: number) => [...cmmsKeys.equipment.details(), id] as const,
    warrantyAlerts: () => [...cmmsKeys.equipment.all, 'warranty_alerts'] as const,
  },
  workOrders: {
    all: ['work_orders'] as const,
    lists: () => [...cmmsKeys.workOrders.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...cmmsKeys.workOrders.lists(), filters] as const,
    details: () => [...cmmsKeys.workOrders.all, 'detail'] as const,
    detail: (id: number) => [...cmmsKeys.workOrders.details(), id] as const,
    parts: (workOrderId: number) => [...cmmsKeys.workOrders.detail(workOrderId), 'parts'] as const,
  },
  vendors: {
    all: ['vendors'] as const,
    lists: () => [...cmmsKeys.vendors.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...cmmsKeys.vendors.lists(), filters] as const,
    details: () => [...cmmsKeys.vendors.all, 'detail'] as const,
    detail: (id: number) => [...cmmsKeys.vendors.details(), id] as const,
  },
  maintenanceSchedules: {
    all: (equipmentId: number) => ['equipment', equipmentId, 'maintenance_schedules'] as const,
  },
}

// ─── Equipment Hooks ──────────────────────────────────────────────────────────

export interface EquipmentFilters {
  property_id?: number
  category?: string
  status?: string
  q?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/name/status/warranty_expires_at)
  sort?: string
  sort_order?: string
}

export function useEquipment(page = 1, filters: EquipmentFilters = {}) {
  return useQuery({
    queryKey: cmmsKeys.equipment.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.property_id) params.property_id = filters.property_id
      if (filters.category) params.category = filters.category
      if (filters.status) params.status = filters.status
      if (filters.q) params.q = filters.q
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<Equipment[]>>('/api/equipment', { params })
      return data
    },
  })
}

export function useEquipmentDetail(id: number) {
  return useQuery({
    queryKey: cmmsKeys.equipment.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Equipment>>(`/api/equipment/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// `enabled` false sends nothing: the sidebar passes it for an account without
// the CMMS module and for the accountant (EquipmentPolicy#warranty_alerts? is
// staff and above), which both 403ed on every page before.
export function useWarrantyAlerts(enabled = true) {
  return useQuery({
    queryKey: cmmsKeys.equipment.warrantyAlerts(),
    enabled,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Equipment[]>>('/api/equipment/warranty_alerts')
      return data
    },
  })
}

interface CreateEquipmentInput {
  name: string
  category: string
  status: string
  property_id: number
  location?: string
  manufacturer?: string
  model_number?: string
  serial_number?: string
  installed_at?: string
  warranty_expires_at?: string
  notes?: string
  image_urls?: string[]
}

export function useCreateEquipment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateEquipmentInput) => {
      const { data } = await api.post<ApiResponse<Equipment>>('/api/equipment', { equipment: input })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, cmmsKeys.equipment.lists())
    },
  })
}

interface UpdateEquipmentInput extends Partial<CreateEquipmentInput> {
  id: number
}

export function useUpdateEquipment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateEquipmentInput) => {
      const { data } = await api.patch<ApiResponse<Equipment>>(`/api/equipment/${id}`, { equipment: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, cmmsKeys.equipment.lists())
      invalidateDetailQueries(queryClient, cmmsKeys.equipment.detail(data.id))
    },
  })
}

export function useDeleteEquipment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/equipment/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, cmmsKeys.equipment.lists())
    },
  })
}

// ─── Maintenance Schedule Hooks ───────────────────────────────────────────────

export function useMaintenanceSchedules(equipmentId: number) {
  return useQuery({
    queryKey: cmmsKeys.maintenanceSchedules.all(equipmentId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<MaintenanceSchedule[]>>(
        `/api/equipment/${equipmentId}/maintenance_schedules`,
        { params: { all: true } }
      )
      return data
    },
    enabled: !!equipmentId,
  })
}

interface CreateMaintenanceScheduleInput {
  equipment_id: number
  title: string
  description?: string
  frequency: string
  next_due_at?: string
  active?: boolean
  assigned_to_id?: number | null
}

export function useCreateMaintenanceSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ equipment_id, ...input }: CreateMaintenanceScheduleInput) => {
      const { data } = await api.post<ApiResponse<MaintenanceSchedule>>(
        `/api/equipment/${equipment_id}/maintenance_schedules`,
        { maintenance_schedule: input }
      )
      return data.data
    },
    onSuccess: (_, variables) => {
      removeListQueries(queryClient, cmmsKeys.maintenanceSchedules.all(variables.equipment_id))
      invalidateDetailQueries(queryClient, cmmsKeys.equipment.detail(variables.equipment_id))
    },
  })
}

interface UpdateMaintenanceScheduleInput {
  id: number
  equipment_id: number
  title?: string
  description?: string
  frequency?: string
  next_due_at?: string
  active?: boolean
  assigned_to_id?: number | null
}

export function useUpdateMaintenanceSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, equipment_id, ...input }: UpdateMaintenanceScheduleInput) => {
      const { data } = await api.patch<ApiResponse<MaintenanceSchedule>>(
        `/api/equipment/${equipment_id}/maintenance_schedules/${id}`,
        { maintenance_schedule: input }
      )
      return data.data
    },
    onSuccess: (_, variables) => {
      removeListQueries(queryClient, cmmsKeys.maintenanceSchedules.all(variables.equipment_id))
      // EquipmentBlueprint :with_schedules_and_orders embeds maintenance_schedules
      invalidateDetailQueries(queryClient, cmmsKeys.equipment.detail(variables.equipment_id))
    },
  })
}

export function useDeleteMaintenanceSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, equipment_id }: { id: number; equipment_id: number }) => {
      await api.delete(`/api/equipment/${equipment_id}/maintenance_schedules/${id}`)
      return { id, equipment_id }
    },
    onSuccess: (_, variables) => {
      removeListQueries(queryClient, cmmsKeys.maintenanceSchedules.all(variables.equipment_id))
      invalidateDetailQueries(queryClient, cmmsKeys.equipment.detail(variables.equipment_id))
    },
  })
}

// ─── Work Order Hooks ─────────────────────────────────────────────────────────

export interface WorkOrderFilters {
  status?: string
  priority?: string
  equipment_id?: number
  assigned_to_id?: number
  q?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/created_at/due_date/priority/status)
  sort?: string
  sort_order?: string
}

export function useWorkOrders(page = 1, filters: WorkOrderFilters = {}) {
  return useQuery({
    queryKey: cmmsKeys.workOrders.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.status) params.status = filters.status
      if (filters.priority) params.priority = filters.priority
      if (filters.equipment_id) params.equipment_id = filters.equipment_id
      if (filters.assigned_to_id) params.assigned_to_id = filters.assigned_to_id
      if (filters.q) params.q = filters.q
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<WorkOrder[]>>('/api/work_orders', { params })
      return data
    },
  })
}

export function useWorkOrderDetail(id: number) {
  return useQuery({
    queryKey: cmmsKeys.workOrders.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<WorkOrder>>(`/api/work_orders/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

interface CreateWorkOrderInput {
  title: string
  description?: string
  priority: string
  status?: string
  // T130-001: the model now requires equipment OR lease, not equipment
  // unconditionally — a move-out deduction work order may carry only a
  // lease link. `null` clears the association (an omitted key would leave
  // whatever the server already had, which matters on update).
  equipment_id?: number | null
  lease_id?: number | null
  vendor_id?: number | null
  assigned_to_id?: number | null
  due_date?: string
  estimated_cost?: number | null
  notes?: string
  image_urls?: string[]
}

// VendorBlueprint :with_details embeds recent_work_orders (last 10);
// EquipmentBlueprint :with_schedules_and_orders embeds recent_work_orders too.
// Any work-order mutation must refresh both parent caches.
function invalidateWorkOrderParents(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrder?: Pick<WorkOrder, 'equipment_id' | 'vendor_id'> | null
) {
  if (workOrder?.equipment_id) {
    invalidateDetailQueries(queryClient, cmmsKeys.equipment.detail(workOrder.equipment_id))
  }
  if (workOrder?.vendor_id) {
    invalidateDetailQueries(queryClient, cmmsKeys.vendors.detail(workOrder.vendor_id))
  }
  // Delete path has no work-order data — broad-invalidate both detail prefixes
  // so any cached vendor/equipment detail showing recent_work_orders refreshes.
  if (!workOrder) {
    invalidateDetailQueries(queryClient, cmmsKeys.equipment.details())
    invalidateDetailQueries(queryClient, cmmsKeys.vendors.details())
  }
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateWorkOrderInput) => {
      const { data } = await api.post<ApiResponse<WorkOrder>>('/api/work_orders', { work_order: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, cmmsKeys.workOrders.lists())
      invalidateWorkOrderParents(queryClient, data)
    },
  })
}

interface UpdateWorkOrderInput extends Partial<CreateWorkOrderInput> {
  id: number
  actual_cost?: number | null
  quality_rating?: number | null
  completed_at?: string
}

export function useUpdateWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateWorkOrderInput) => {
      const { data } = await api.patch<ApiResponse<WorkOrder>>(`/api/work_orders/${id}`, { work_order: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, cmmsKeys.workOrders.lists())
      invalidateDetailQueries(queryClient, cmmsKeys.workOrders.detail(data.id))
      invalidateWorkOrderParents(queryClient, data)
    },
  })
}

export function useDeleteWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/work_orders/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, cmmsKeys.workOrders.lists())
      invalidateWorkOrderParents(queryClient, null)
    },
  })
}

// ─── Vendor Hooks ─────────────────────────────────────────────────────────────

export interface VendorFilters {
  active?: boolean
  q?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/created_at/name/avg_score)
  sort?: string
  sort_order?: string
}

export function useVendors(page = 1, filters: VendorFilters = {}) {
  return useQuery({
    queryKey: cmmsKeys.vendors.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.active !== undefined) params.active = filters.active
      if (filters.q) params.q = filters.q
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<Vendor[]>>('/api/vendors', { params })
      return data
    },
  })
}

export function useVendorDetail(id: number) {
  return useQuery({
    queryKey: cmmsKeys.vendors.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Vendor>>(`/api/vendors/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

interface CreateVendorInput {
  name: string
  contact_name?: string
  phone?: string
  email?: string
  address?: string
  specialties?: string[]
  notes?: string
  active?: boolean
}

export function useCreateVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateVendorInput) => {
      const { data } = await api.post<ApiResponse<Vendor>>('/api/vendors', { vendor: input })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, cmmsKeys.vendors.lists())
    },
  })
}

interface UpdateVendorInput extends Partial<CreateVendorInput> {
  id: number
}

export function useUpdateVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateVendorInput) => {
      const { data } = await api.patch<ApiResponse<Vendor>>(`/api/vendors/${id}`, { vendor: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, cmmsKeys.vendors.lists())
      invalidateDetailQueries(queryClient, cmmsKeys.vendors.detail(data.id))
    },
  })
}

export function useDeleteVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/vendors/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, cmmsKeys.vendors.lists())
    },
  })
}
